import { _ as _applyDecoratedDescriptor, a as _initializerDefineProperty } from '../_rollupPluginBabelHelpers-3b0ba74f.js';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action, set } from '@ember/object';
import { inject } from '@ember/service';
import { task, timeout } from 'ember-concurrency';
import { next } from '@ember/runloop';
import { paginatorPageLists } from 'beebee-base/lib/utility';
import { precompileTemplate } from '@ember/template-compilation';
import { setComponentTemplate } from '@ember/component';

var TEMPLATE = precompileTemplate("<BasicDropdown\n  @matchTriggerWidth={{true}}\n  @verticalPosition={{this._verticalPosition}}\n  @destinationElement={{this.destinationElement}}\n  @onOpen={{this.onDropdownOpen}}\n  @onClose={{this.onDropdownClose}}\n  @registerAPI={{this.registerDropdownAPI}}\n  data-test-on-dropdown-open\n  data-test-on-dropdown-close\n  as |dd|\n>\n  {{! ...attributes lands on the trigger, the control a user actually clicks to\n      start an association. Without it, every attribute a host passes to\n      <SelectAssociation> — including the `data-test-*` hook that names its\n      @action site — reached no element at all, because this template\'s root is\n      the <BasicDropdown> component and nothing else splatted. }}\n  <dd.Trigger\n    class=\'tail-select no-classes multiple {{if dd.isOpen \"active\"}}\'\n    ...attributes\n    {{did-insert this.onInsert}}\n  >\n    <div class=\'select-label\'>\n      <span class=\'label-inner\'>\n        {{#if this.searchItem.isRunning}}\n          <BbSpinner />\n        {{else}}\n          {{this.labelSearchInput}}\n        {{/if}}\n      </span>\n    </div>\n  </dd.Trigger>\n\n  <dd.Content class=\'tail-select active tail-select-portal\'>\n    <div class=\'select-dropdown\'>\n      <div\n        class=\'dropdown-box\'\n        data-test-prevent-input-blur\n        {{on \'mousedown\' this.preventInputBlur}}\n      >\n        <form\n          class=\'dropdown-search search-reset\'\n          data-test-handle-submit\n          {{on \'submit\' this.handleSubmit}}\n        >\n          <div class=\'input-search\'>\n            <Input\n              @type=\'search\'\n              @value={{this.searchValue}}\n              placeholder=\'{{this.labelSearchInput}}\'\n              class=\'search-input\'\n              data-test-search-input\n              {{on \'input\' (perform this.searchInputTask)}}\n              {{did-insert this.focusSearchInput}}\n            />\n          </div>\n          <button class=\'reset-icon\' type=\'button\' data-test-close {{on \'click\' this.close}}>\n            <FaIcon @icon=\'times\' />\n          </button>\n        </form>\n\n        <div class=\'dropdown-inner\'>\n          <div class=\'grid-x dropdown-header\'>\n            <div class=\'cell shrink\'>\n              <span class=\'result\'>\n                {{#if this.items}}\n                  {{t \'search_results\' count=this.items.meta.total_items}}\n                {{/if}}\n              </span>\n            </div>\n            <SelectAssociation::FilterBar\n              @stateToLoop={{this.stateToLoop}}\n              @isOpen={{this.filter}}\n              @statusSelected={{this.statusSelected}}\n              @onToggleFilter={{this.toggleFilter}}\n              @onSelectStatus={{this.selectStatus}}\n              data-test-toggle-filter\n              data-test-select-status\n            />\n          </div>\n\n          <SelectAssociation::ResultList\n            @isLoading={{this.searchItem.isRunning}}\n            @items={{this.items}}\n            @focusIndex={{this.focusIndex}}\n            @pageLists={{this.pageLists}}\n            @searchValue={{this.searchValue}}\n            @less3Char={{this.less3Char}}\n            @searchError={{this.searchError}}\n            @showThumbnail={{this.showThumbnail}}\n            @applicationController={{@applicationController}}\n            @onSetItem={{this.setItem}}\n            @onSetPage={{this.setPage}}\n            @onShowAll={{this.showAll}}\n            data-test-set-item\n            data-test-set-page\n            data-test-show-all\n          />\n\n          {{#if this.searchValue}}\n            {{#if this.showAddButton}}\n              <button\n                type=\'button\'\n                class=\'button expanded flat add-new clearfix\'\n                title=\'{{t \"add\"}} {{this.searchValue}}\'\n                data-test-add-new-item\n                {{on \'click\' (perform this.addNewItemTask)}}\n              >\n                <FaIcon @icon=\'plus\' />\n                {{t \'add\'}} \"{{this.searchValue}}\"\n              </button>\n            {{/if}}\n          {{/if}}\n        </div>\n      </div>\n    </div>\n  </dd.Content>\n</BasicDropdown>\n\n{{on-key \'ArrowDown\' this.downKey firstResponder=true}}\n{{on-key \'ArrowUp\' this.upKey firstResponder=true}}\n{{on-key \'ArrowLeft\' this.leftKey firstResponder=true}}\n{{on-key \'ArrowRight\' this.rightKey firstResponder=true}}\n{{on-key \'Enter\' this.enterKey firstResponder=true}}\n{{on-key \'Escape\' this.escKey firstResponder=true}}");

var _dec, _dec2, _dec3, _dec4, _dec5, _class, _descriptor, _descriptor2, _descriptor3, _descriptor4, _descriptor5, _descriptor6, _descriptor7, _descriptor8, _descriptor9, _descriptor0, _descriptor1, _descriptor10, _descriptor11, _descriptor12, _descriptor13;
const DEBOUNCE_MS = 500;
let SelectAssociationComponent = (_dec = task({
  restartable: true
}), _dec2 = task({
  drop: true
}), _dec3 = task({
  restartable: true
}), _dec4 = task({
  restartable: true
}), _dec5 = task({
  restartable: true
}), _class = class SelectAssociationComponent extends Component {
  constructor(owner, args) {
    super(owner, args);
    _initializerDefineProperty(this, "session", _descriptor, this);
    _initializerDefineProperty(this, "intl", _descriptor2, this);
    _initializerDefineProperty(this, "store", _descriptor3, this);
    // --- Tracked state ---
    _initializerDefineProperty(this, "searchValue", _descriptor4, this);
    _initializerDefineProperty(this, "items", _descriptor5, this);
    _initializerDefineProperty(this, "itemSelected", _descriptor6, this);
    _initializerDefineProperty(this, "searchError", _descriptor7, this);
    _initializerDefineProperty(this, "pageLists", _descriptor8, this);
    _initializerDefineProperty(this, "page", _descriptor9, this);
    _initializerDefineProperty(this, "focusIndex", _descriptor0, this);
    _initializerDefineProperty(this, "statusSelected", _descriptor1, this);
    _initializerDefineProperty(this, "filter", _descriptor10, this);
    _initializerDefineProperty(this, "stopClosing", _descriptor11, this);
    _initializerDefineProperty(this, "ddAPI", _descriptor12, this);
    _initializerDefineProperty(this, "_verticalPosition", _descriptor13, this);
    if (args.searchValue !== undefined) {
      this.searchValue = args.searchValue;
    }
    const id = 'select-association-wormhole';
    let el = document.getElementById(id);
    if (!el) {
      el = document.createElement('div');
      el.id = id;
      document.body.appendChild(el);
    }
    this._destinationElement = el;
  }

  // --- Getters ---

  get destinationElement() {
    return this._destinationElement;
  }
  get isOpen() {
    return this.ddAPI?.isOpen ?? false;
  }
  get labelSearchInput() {
    return this.args.wrapLabelSearchInput || this.intl.t('search...');
  }
  get showThumbnail() {
    return this.args.showThumbnail ?? true;
  }
  get showRelated() {
    return this.args.showRelated ?? true;
  }
  get addNew() {
    return this.args.addNew ?? true;
  }
  get stateToLoop() {
    const organization = this.session.me?.organization;
    return organization?.get?.(`${this.args.relatedModelType}_states`) ?? null;
  }
  get less3Char() {
    // searchValue can be null (onDropdownOpen/_setItemTask reset) — the old
    // `!== undefined` guard let null through into `.length` (TypeError).
    const sv = this.searchValue || '';
    return sv.length > 0 && sv.length < 3;
  }
  get showAddButton() {
    const isClient = this.session.me?.role?.is_client;
    const isContactModel = this.args.relatedModelType === 'contact';
    if (isContactModel) {
      return !isClient && this.addNew;
    }
    return this.addNew;
  }

  // --- Setup ---

  onInsert() {
    this.items = [];
    this.statusSelected = this.args.status || null;
    this.pageLists = null;
    this.focusIndex = -1;
  }

  // --- BasicDropdown integration ---

  registerDropdownAPI(api) {
    this.ddAPI = api;
  }
  onDropdownOpen() {
    // Cancel any in-flight search: a run still pending from before the
    // dropdown closed would repopulate items after this reset.
    this.searchItem.cancelAll();
    this.items = [];
    this.statusSelected = this.args.status || null;
    this.pageLists = null;
    this.searchValue = null;
    this.page = 1;
    this.searchError = false;
    this.focusIndex = -1;

    // Flip: if not enough space below the trigger, open above.
    // Use visualViewport when available (accounts for virtual keyboard on mobile).
    const trigger = this.ddAPI?.actions?.getTriggerElement?.();
    if (trigger) {
      const MIN_DROP_HEIGHT = 220;
      const triggerRect = trigger.getBoundingClientRect();
      const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
      const spaceBelow = viewportHeight - triggerRect.bottom;
      this._verticalPosition = spaceBelow < MIN_DROP_HEIGHT ? 'above' : 'below';
    }

    // Force reposition after CSS is applied to avoid initial misplacement.
    next(this, () => {
      if (!this.isDestroying && !this.isDestroyed) {
        this.ddAPI?.actions?.reposition?.();
      }
    });

    // On mobile, the virtual keyboard reduces the viewport after the input is
    // focused. Listen to visualViewport resize to reposition the dropdown.
    this._startVisualViewportListener();
  }
  onDropdownClose() {
    this._stopVisualViewportListener();
  }
  _startVisualViewportListener() {
    if (!window.visualViewport || this._vpResizeHandler) return;
    this._vpResizeHandler = () => {
      if (!this.isDestroying && !this.isDestroyed) {
        this.ddAPI?.actions?.reposition?.();
      }
    };
    window.visualViewport.addEventListener('resize', this._vpResizeHandler);
  }
  _stopVisualViewportListener() {
    if (!window.visualViewport || !this._vpResizeHandler) return;
    window.visualViewport.removeEventListener('resize', this._vpResizeHandler);
    this._vpResizeHandler = null;
  }
  focusSearchInput(element) {
    next(this, () => {
      if (!this.isDestroying && !this.isDestroyed) {
        // iOS Safari may ignore preventScroll — compensate by restoring scroll.
        const scrollY = window.scrollY;
        element.focus({
          preventScroll: true
        });
        requestAnimationFrame(() => {
          if (Math.abs(window.scrollY - scrollY) > 10) {
            window.scrollTo({
              top: scrollY,
              behavior: 'instant'
            });
          }
        });
      }
    });
  }
  willDestroy() {
    super.willDestroy(...arguments);
    this._stopVisualViewportListener();
  }

  // --- Search & Data Tasks ---

  *searchItem(showAll = false) {
    let q = this.searchValue ?? '';
    if (this.args.appendQuery) {
      q = q ? `${q} ${this.args.appendQuery}` : this.args.appendQuery;
    }
    if (showAll) {
      this.page = 1;
    }
    if (!showAll && q.length > 0 && q.length < 3 && isNaN(q)) {
      this.items = [];
      this.pageLists = null;
      return;
    }
    yield timeout(DEBOUNCE_MS);
    const paramsToQuery = {
      q,
      xpag: 5,
      status: this.statusSelected?.id,
      pag: this.page,
      document_type: this.args.documentType,
      id: this.args.custom_field?.organization_custom_field?.id,
      contact_id: this.args.filter_by_contact,
      product_id: this.args.filter_by_product,
      project_id: this.args.filter_by_project,
      role: this.args.role?.id
    };
    this.pageLists = null;
    this.searchError = false;
    try {
      const items = yield this.store.query(this.args.relatedModelType, paramsToQuery);
      this.items = items;
      if (items?.meta?.total_pages) {
        this.pageLists = paginatorPageLists(items.meta.total_pages, this.page);
      }
    } catch {
      this.items = [];
      this.searchError = true;
    }
  }
  *addNewItemTask() {
    const relatedModelType = this.args.relatedModelType;
    const modelStatesName = `${relatedModelType}_states`;
    const statusName = `status_${this.args.defaultStatusName ? 'name' : 'default'}`;
    const statusValue = this.args.defaultStatusName || true;
    const orgModelState = relatedModelType ? this.session.me?.organization?.[modelStatesName] : null;
    const orgDefaultState = orgModelState ? orgModelState.filter(item => item[statusName] === statusValue)?.[0] : null;

    // A panel scoped to a status (`@status` — the room / electromedical /
    // specialists panels on product-detail, one panel per contact state in
    // related-contacts) must create the record IN THAT STATUS. Seeding it from
    // the organization's `status_default` instead made has-many's own status
    // filter (has-many.js:106) drop the record that had just been created and
    // linked: no chip appeared, the operator read it as a failed add and created
    // the record again — duplicates, each of them really linked to the host.
    const defaultState = this.args.status ?? orgDefaultState;
    let newRecord;
    try {
      newRecord = yield this.args.applicationController.newRecordFactory.createNewModel.perform(relatedModelType, null, defaultState, false);
    } catch {
      return;
    }
    newRecord.role = this.args.role;
    if (relatedModelType === 'contact') {
      const fullName = this.searchValue || '';
      const names = fullName.split(' ');
      const last = names.pop();
      const first = names.join(' ');
      newRecord.setProperties({
        first_name: first || last,
        last_name: first ? last : ''
      });
    }
    let itemAPI;
    try {
      itemAPI = yield this.args.applicationController.saveRecordRelated(newRecord);
    } catch {
      return;
    }
    if (this.isDestroying || this.isDestroyed) return;

    // saveRecordRelated resolves false on failure: no id, nothing to peek
    // (peekRecord with undefined id asserts).
    const newId = itemAPI?.[0]?.data?.id;
    const itemStore = newId ? this.store.peekRecord(relatedModelType, newId) : null;
    if (itemStore) {
      this.ddAPI?.actions.close();
      if (typeof this.args.action === 'function') {
        this.args.action(itemStore.id);
      }
      if (defaultState) {
        const modelName = itemStore.constructor.modelName;
        const obj = {
          title: `${this.intl.t('edit')} ${modelName === 'invoice' ? this.args.documentType : modelName}`,
          componentName: this.session.me.get(`role.application.${modelName}_edit`),
          item: itemStore
        };
        if (this.args.updateModelAfterSave) {
          obj.updateModelAfterSave = {
            action: this.args.updateModelAfterSave,
            item: this.args.modelToUpdate
          };
        }
        const appController = this.args.applicationController;
        if (appController) {
          set(appController, 'modalComponents', [...(appController.modalComponents || []), obj]);
        }
      }
    }
  }
  *closeDropDownTask() {
    yield timeout(1000);
    if (this.isDestroying || this.isDestroyed) return;
    if (this.stopClosing) {
      this.stopClosing = false;
    } else {
      this.ddAPI?.actions.close();
    }
  }
  *_setItemTask(item) {
    yield timeout(50);
    if (this.isDestroying || this.isDestroyed) return;
    this.searchValue = this.showRelated ? null : item.label;
    this.ddAPI?.actions.close();
    this.itemSelected = item;
    if (typeof this.args.action === 'function') {
      this.args.action(item.id);
    }
  }
  *searchInputTask() {
    yield timeout(50);
    if (this.isDestroying || this.isDestroyed) return;
    if (this.searchValue && this.searchValue !== '') {
      this.page = 1;
      this.searchItem.perform();
    } else {
      // Also cancel the debounced search still holding the previous query,
      // or it would resume after its timeout and overwrite the cleared list.
      this.searchItem.cancelAll();
      this.items = [];
      this.pageLists = null;
    }
  }

  // --- Actions ---

  handleSubmit(event) {
    event.preventDefault();
  }
  close() {
    this.ddAPI?.actions.close();
  }
  noClose() {
    this.stopClosing = true;
  }

  /**
   * Prevents the input from losing focus when the user clicks
   * anywhere inside the dropdown (results, buttons, pagination, etc.).
   */
  preventInputBlur(event) {
    if (event.target.tagName !== 'INPUT') {
      event.preventDefault();
    }
  }

  /**
   * "Show all results" — a named action rather than an inline
   * `{{fn (perform this.searchItem) true}}` in the template, so the site carries
   * its own `data-test-show-all` hook and can be driven (and credited) on its own
   * instead of sharing the invocation's first hook with @onSetItem/@onSetPage.
   */
  showAll() {
    this.searchItem.perform(true);
  }
  setPage(page) {
    this.page = page;
    this.searchItem.perform();
  }
  toggleFilter() {
    this.filter = !this.filter;
  }
  selectStatus(item) {
    this.statusSelected = item === this.statusSelected ? null : item;
    this.page = 1;
    this.focusIndex = -1;
    this.searchItem.perform();
  }
  setItem(item) {
    this._setItemTask.perform(item);
  }

  // --- Keyboard Navigation ---

  upKey() {
    if (!this.isOpen) return;
    this.focusItemAt(this.focusIndex - 1);
  }
  downKey() {
    if (!this.isOpen) return;
    this.focusItemAt(this.focusIndex + 1);
  }
  escKey() {
    this.ddAPI?.actions.close();
  }
  leftKey() {
    if (!this.isOpen) return;
    let page = this.page - 1;
    if (page <= 0) page = 1;
    this.page = page;
    this.focusIndex = -1;
    this.searchItem.perform();
  }
  rightKey() {
    if (!this.isOpen) return;
    const nextPage = this.page + 1;
    if (this.items?.meta?.total_pages && this.items.meta.total_pages >= nextPage) {
      this.page = nextPage;
      this.focusIndex = -1;
      this.searchItem.perform();
    }
  }
  enterKey() {
    if (!this.isOpen || !this.items?.length) return;
    const itemSelected = this.items.objectAt ? this.items.objectAt(this.focusIndex) : this.items[this.focusIndex];
    if (itemSelected) {
      this._setItemTask.perform(itemSelected);
    }
  }
  focusItemAt(index) {
    if (!this.isOpen) return;
    const len = this.items?.length ?? 0;
    if (len === 0) return;
    if (index < 0) {
      index = len - 1;
    } else if (index >= len) {
      index = 0;
    }
    this.focusIndex = index;
  }
}, _descriptor = _applyDecoratedDescriptor(_class.prototype, "session", [inject], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor2 = _applyDecoratedDescriptor(_class.prototype, "intl", [inject], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor3 = _applyDecoratedDescriptor(_class.prototype, "store", [inject], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor4 = _applyDecoratedDescriptor(_class.prototype, "searchValue", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return '';
  }
}), _descriptor5 = _applyDecoratedDescriptor(_class.prototype, "items", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return null;
  }
}), _descriptor6 = _applyDecoratedDescriptor(_class.prototype, "itemSelected", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return null;
  }
}), _descriptor7 = _applyDecoratedDescriptor(_class.prototype, "searchError", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return false;
  }
}), _descriptor8 = _applyDecoratedDescriptor(_class.prototype, "pageLists", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return null;
  }
}), _descriptor9 = _applyDecoratedDescriptor(_class.prototype, "page", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return 1;
  }
}), _descriptor0 = _applyDecoratedDescriptor(_class.prototype, "focusIndex", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return -1;
  }
}), _descriptor1 = _applyDecoratedDescriptor(_class.prototype, "statusSelected", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return null;
  }
}), _descriptor10 = _applyDecoratedDescriptor(_class.prototype, "filter", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return false;
  }
}), _descriptor11 = _applyDecoratedDescriptor(_class.prototype, "stopClosing", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return false;
  }
}), _descriptor12 = _applyDecoratedDescriptor(_class.prototype, "ddAPI", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return null;
  }
}), _descriptor13 = _applyDecoratedDescriptor(_class.prototype, "_verticalPosition", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return 'below';
  }
}), _applyDecoratedDescriptor(_class.prototype, "onInsert", [action], Object.getOwnPropertyDescriptor(_class.prototype, "onInsert"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "registerDropdownAPI", [action], Object.getOwnPropertyDescriptor(_class.prototype, "registerDropdownAPI"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "onDropdownOpen", [action], Object.getOwnPropertyDescriptor(_class.prototype, "onDropdownOpen"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "onDropdownClose", [action], Object.getOwnPropertyDescriptor(_class.prototype, "onDropdownClose"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "focusSearchInput", [action], Object.getOwnPropertyDescriptor(_class.prototype, "focusSearchInput"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "searchItem", [_dec], Object.getOwnPropertyDescriptor(_class.prototype, "searchItem"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "addNewItemTask", [_dec2], Object.getOwnPropertyDescriptor(_class.prototype, "addNewItemTask"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "closeDropDownTask", [_dec3], Object.getOwnPropertyDescriptor(_class.prototype, "closeDropDownTask"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "_setItemTask", [_dec4], Object.getOwnPropertyDescriptor(_class.prototype, "_setItemTask"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "searchInputTask", [_dec5], Object.getOwnPropertyDescriptor(_class.prototype, "searchInputTask"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "handleSubmit", [action], Object.getOwnPropertyDescriptor(_class.prototype, "handleSubmit"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "close", [action], Object.getOwnPropertyDescriptor(_class.prototype, "close"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "noClose", [action], Object.getOwnPropertyDescriptor(_class.prototype, "noClose"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "preventInputBlur", [action], Object.getOwnPropertyDescriptor(_class.prototype, "preventInputBlur"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "showAll", [action], Object.getOwnPropertyDescriptor(_class.prototype, "showAll"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "setPage", [action], Object.getOwnPropertyDescriptor(_class.prototype, "setPage"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "toggleFilter", [action], Object.getOwnPropertyDescriptor(_class.prototype, "toggleFilter"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "selectStatus", [action], Object.getOwnPropertyDescriptor(_class.prototype, "selectStatus"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "setItem", [action], Object.getOwnPropertyDescriptor(_class.prototype, "setItem"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "upKey", [action], Object.getOwnPropertyDescriptor(_class.prototype, "upKey"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "downKey", [action], Object.getOwnPropertyDescriptor(_class.prototype, "downKey"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "escKey", [action], Object.getOwnPropertyDescriptor(_class.prototype, "escKey"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "leftKey", [action], Object.getOwnPropertyDescriptor(_class.prototype, "leftKey"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "rightKey", [action], Object.getOwnPropertyDescriptor(_class.prototype, "rightKey"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "enterKey", [action], Object.getOwnPropertyDescriptor(_class.prototype, "enterKey"), _class.prototype), _class);
setComponentTemplate(TEMPLATE, SelectAssociationComponent);

export { SelectAssociationComponent as default };
//# sourceMappingURL=select-association.js.map
